import {
  ApproveOrderCandidatePayload,
  CreateInvoicePayload,
  CreatePurchaseOrderManualPayload,
  CreateSalesOrderManualPayload,
  DeleteInvoicePayload,
  DeleteInventoryPayload,
  DeletePaymentPayload,
  LogSensitiveRevealPayload,
  RecordAccountIssuePayload,
  RecordPaymentPayload,
  RecordStockCheckPayload,
  RejectOrderCandidatePayload,
  UpdateInvoicePayload,
  UpdateOrderCandidatePayload,
} from "@/core";
import { serviceHost } from "@/services";
import { useUserStore } from "@/stores/user";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const functionsService = serviceHost.getFunctionsService();

export const useApproveOrderCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ApproveOrderCandidatePayload) =>
      functionsService.approveOrderCandidate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders"] });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
    },
  });
};

export const useRejectOrderCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RejectOrderCandidatePayload) =>
      functionsService.rejectOrderCandidate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-candidates"] });
    },
  });
};

export const useUpdateOrderCandidate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateOrderCandidatePayload) =>
      functionsService.updateOrderCandidate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useCreatePurchaseOrderManual = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePurchaseOrderManualPayload) =>
      functionsService.createPurchaseOrderManual(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useCreateSalesOrderManual = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSalesOrderManualPayload) =>
      functionsService.createSalesOrderManual(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-orders"] });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useRecordStockCheck = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RecordStockCheckPayload) =>
      functionsService.recordStockCheck(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["stock-checks"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useRecordAccountIssue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RecordAccountIssuePayload) =>
      functionsService.recordAccountIssue(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["account-issues"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useCreateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateInvoicePayload) =>
      functionsService.createInvoice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders"] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateInvoicePayload) =>
      functionsService.updateInvoice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DeleteInvoicePayload) =>
      functionsService.deleteInvoice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useRecordPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RecordPaymentPayload) =>
      functionsService.recordPayment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders"] });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useDeletePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DeletePaymentPayload) =>
      functionsService.deletePayment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders"] });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useDeleteInventory = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation({
    mutationFn: async (payload: DeleteInventoryPayload) => {
      if (!hasFullAccess()) {
        throw new Error("Access denied: Full access required to delete inventories");
      }
      return functionsService.deleteInventory(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
      queryClient.invalidateQueries({ queryKey: ["sales-orders"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["inventory-ledger"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};

export const useLogSensitiveReveal = () => {
  return useMutation({
    mutationFn: (payload: LogSensitiveRevealPayload) =>
      functionsService.logSensitiveReveal(payload),
  });
};
